import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Bot, PhoneCall, Loader2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import ModuleDemoVideo from '../components/ModuleDemoVideo';

const VoiceCampaigns = () => {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [creating, setCreating] = useState(false);
  const [starting, setStarting] = useState(false);

  const [formName, setFormName] = useState('');
  const [formBotId, setFormBotId] = useState('');
  const [formPhones, setFormPhones] = useState('');

  const token = localStorage.getItem('token');

  const fetchBotsAndCampaigns = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [botsRes, campaignsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/my/bots`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/campaigns`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const [botsData, campaignsData] = await Promise.all([
        botsRes.json().catch(() => []),
        campaignsRes.json().catch(() => []),
      ]);
      if (!botsRes.ok) throw new Error(botsData.detail || 'Failed to load bots');
      if (!campaignsRes.ok) throw new Error(campaignsData.detail || 'Failed to load campaigns');

      const voiceBots = (botsData || []).filter((b) => b.type === 'voice');
      setBots(voiceBots);
      setCampaigns(campaignsData || []);
      if (!formBotId && voiceBots.length > 0) {
        setFormBotId(voiceBots[0].id);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBotsAndCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCampaignDetail = async (id) => {
    if (!token) return;
    setLoadingDetail(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/voice/campaigns/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || 'Failed to load campaign detail');
      setCampaignDetail(data);
      setSelectedCampaign(id);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load campaign detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!token || !formName.trim() || !formBotId || !formPhones.trim()) return;

    const lines = formPhones
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length === 0) {
      toast.error('Please enter at least one phone number');
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voice/campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formName.trim(),
          bot_id: formBotId,
          phone_numbers: lines,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || 'Failed to create campaign');
      toast.success('Campaign created');
      setFormName('');
      setFormPhones('');
      await fetchBotsAndCampaigns();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleStart = async (id) => {
    if (!token) return;
    setStarting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/voice/campaigns/${id}/start`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || 'Failed to start campaign');
      toast.success('Campaign started');
      await fetchBotsAndCampaigns();
      await fetchCampaignDetail(id);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to start campaign');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center">
                <PhoneCall className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Voice Campaigns</h1>
                <p className="text-xs text-zinc-500">Run outbound voice campaigns using your Voice Bots</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-4">
        <ModuleDemoVideo moduleKey="voice-campaigns" className="mb-0" />
        <div className="grid grid-cols-4 gap-6">
        <section className="col-span-2 space-y-4">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <h2 className="text-sm font-semibold mb-2">Create Campaign</h2>
            {bots.length === 0 ? (
              <p className="text-xs text-zinc-500">
                You don&apos;t have any Voice Bots yet. Create one from AI Tools &gt; Voice Bots first.
              </p>
            ) : (
              <form onSubmit={handleCreate} className="space-y-3 text-xs">
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Campaign Name</label>
                  <Input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. Missed Call Follow-up"
                    className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">Voice Bot</label>
                  <select
                    value={formBotId}
                    onChange={(e) => setFormBotId(e.target.value)}
                    className="w-full h-8 rounded-md bg-zinc-900 border border-zinc-700 px-2 text-xs focus:outline-none focus:border-cyan-500"
                  >
                    {bots.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-400 mb-1">
                    Phone Numbers (one per line, E.164 recommended e.g. +14155550123)
                  </label>
                  <Textarea
                    value={formPhones}
                    onChange={(e) => setFormPhones(e.target.value)}
                    rows={5}
                    placeholder={'+14155550123\n+441234567890'}
                    className="text-xs bg-zinc-900 border-zinc-700 focus:border-cyan-500"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    className="h-8 px-3 text-xs bg-cyan-600 hover:bg-cyan-500"
                    disabled={creating}
                  >
                    {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create Campaign'}
                  </Button>
                </div>
              </form>
            )}
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold">Campaigns</h2>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[11px] text-zinc-400 hover:text-white"
                onClick={fetchBotsAndCampaigns}
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            {campaigns.length === 0 ? (
              <p className="text-xs text-zinc-500">No campaigns yet. Create one above.</p>
            ) : (
              <div className="border border-zinc-800 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <Table className="w-full text-xs">
                    <TableHeader className="bg-zinc-900">
                      <TableRow>
                        <TableHead className="px-3 py-2 text-left">Name</TableHead>
                        <TableHead className="px-3 py-2 text-left">Bot</TableHead>
                        <TableHead className="px-3 py-2 text-left">Status</TableHead>
                        <TableHead className="px-3 py-2 text-left">Created</TableHead>
                        <TableHead className="px-3 py-2 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {campaigns.map((c) => {
                        const bot = bots.find((b) => b.id === c.bot_id);
                        return (
                          <TableRow
                            key={c.id}
                            className="border-t border-zinc-800 cursor-pointer hover:bg-zinc-900/60"
                            onClick={() => fetchCampaignDetail(c.id)}
                          >
                            <TableCell className="px-3 py-2 whitespace-nowrap">{c.name}</TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap">{bot?.name || c.bot_id}</TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-[11px] uppercase tracking-wide text-zinc-400">
                              {c.status}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-[11px] text-zinc-500">
                              {c.created_at ? new Date(c.created_at).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-[11px] border-cyan-600 text-cyan-400 hover:bg-cyan-600/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStart(c.id);
                                }}
                                disabled={starting || c.status === 'completed' || c.status === 'running'}
                              >
                                {starting ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : c.status === 'draft' ? (
                                  'Start'
                                ) : (
                                  'Run Again'
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="col-span-2">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 h-full flex flex-col">
            <h2 className="text-sm font-semibold mb-2">Campaign Detail</h2>
            {loadingDetail ? (
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading detail...
              </div>
            ) : !campaignDetail ? (
              <p className="text-xs text-zinc-500">Select a campaign to see call statuses.</p>
            ) : (
              <div className="flex flex-col gap-3 text-xs h-full">
                <div className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/50">
                  <p className="text-sm font-semibold mb-1">{campaignDetail.campaign.name}</p>
                  <p className="text-[11px] text-zinc-400">
                    Status: <span className="uppercase tracking-wide">{campaignDetail.campaign.status}</span>
                  </p>
                  <p className="text-[11px] text-zinc-400">
                    Total numbers: {campaignDetail.campaign.total_numbers}
                  </p>
                  {campaignDetail.campaign.started_at && (
                    <p className="text-[11px] text-zinc-500">
                      Started: {new Date(campaignDetail.campaign.started_at).toLocaleString()}
                    </p>
                  )}
                  {campaignDetail.campaign.completed_at && (
                    <p className="text-[11px] text-zinc-500">
                      Completed: {new Date(campaignDetail.campaign.completed_at).toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="border border-zinc-800 rounded-lg flex-1 overflow-hidden flex flex-col">
                  <div className="px-3 py-2 border-b border-zinc-800 bg-zinc-950/70 text-[11px] text-zinc-400">
                    Calls
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {campaignDetail.calls.length === 0 ? (
                      <p className="text-[11px] text-zinc-500 p-3">No calls recorded yet.</p>
                    ) : (
                      <Table className="w-full text-[11px]">
                        <TableHeader className="bg-zinc-900/70">
                          <TableRow>
                            <TableHead className="px-3 py-2 text-left">Phone</TableHead>
                            <TableHead className="px-3 py-2 text-left">Status</TableHead>
                            <TableHead className="px-3 py-2 text-left">Call SID</TableHead>
                            <TableHead className="px-3 py-2 text-left">Updated</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {campaignDetail.calls.map((call) => (
                            <TableRow key={call.id} className="border-t border-zinc-800">
                              <TableCell className="px-3 py-2 whitespace-nowrap">
                                {call.phone_number}
                              </TableCell>
                              <TableCell className="px-3 py-2 whitespace-nowrap text-[10px] uppercase tracking-wide text-zinc-400">
                                {call.status}
                              </TableCell>
                              <TableCell className="px-3 py-2 whitespace-nowrap text-[10px] text-zinc-500 max-w-[160px] truncate">
                                {call.twilio_call_sid || '-'}
                              </TableCell>
                              <TableCell className="px-3 py-2 whitespace-nowrap text-[10px] text-zinc-500">
                                {call.updated_at ? new Date(call.updated_at).toLocaleString() : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        </div>
      </main>
    </div>
  );
};

export default VoiceCampaigns;
